import React, { useState, useRef, useEffect } from "react";
import "./Home.css";
import {
  Button,
  Modal,
  Box,
  CircularProgress,
  Typography,
  TextField,
  Divider,
} from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

function Home() {
  const videoRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [vNum, setVNum] = useState("");
  const [croppedImage, setCroppedImage] = useState(null);
  const [showCameraButton, setShowCameraButton] = useState(false);

  // Function to detect if the device is a laptop
  const isLaptop = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
      !/mobile|android|iphone|ipad|tablet|ipod|kindle|silk|playbook/i.test(
        userAgent
      ) &&
      /win|mac|linux|ubuntu/i.test(userAgent)
    );
  };

  // Function to detect if the device is iOS
  const isIOS = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/i.test(userAgent);
  };

  const startCamera = () => {
    const constraints = {
      video: {
        facingMode: isLaptop() ? "user" : "environment",
      },
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play(); // Explicitly call play to ensure playback
          setShowCameraButton(false); // Hide the button after starting the camera
        }
      })
      .catch((err) => {
        alert("Camera access denied or not available!");
        console.error("Camera error:", err);
      });
  };

  useEffect(() => {
    if (isIOS()) {
      // For iOS, show the button and wait for user interaction
      setShowCameraButton(true);
    } else {
      // For non-iOS devices, start the camera automatically
      startCamera();
    }

    // Cleanup: Stop the stream when the component unmounts
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, []);

  const captureImageFromVideo = () => {
    const canvas = document.createElement("canvas");
    const video = videoRef.current;
    const ctx = canvas.getContext("2d");

    // Define the crop area based on scanner-guide clip-path (15% to 85% x, 35% to 65% y)
    const cropX = video.videoWidth * 0.15;
    const cropY = video.videoHeight * 0.35;
    const cropWidth = video.videoWidth * (0.85 - 0.15);
    const cropHeight = video.videoHeight * (0.65 - 0.35);

    // Set canvas size to original cropped area for display
    const displayCanvas = document.createElement("canvas");
    displayCanvas.width = cropWidth;
    displayCanvas.height = cropHeight;
    const displayCtx = displayCanvas.getContext("2d");
    displayCtx.drawImage(
      video,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );
    const displayImage = displayCanvas.toDataURL("image/png");

    // Set canvas size to resized dimensions (100px width, auto height) for API
    const targetWidth = 100;
    const targetHeight = (cropHeight / cropWidth) * targetWidth;
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // Draw the cropped portion of the video onto the resized canvas
    ctx.drawImage(
      video,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      targetWidth,
      targetHeight
    );

    return {
      displayImage,
      apiImage: canvas.toDataURL("image/jpeg", 0.7),
    };
  };

  const checkVehicleExistence = async (plate) => {
    try {
      const docRef = doc(db, "vehiclemaster", plate);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        window.location.href = `/receipt?vNum=${plate}`;
      } else {
        window.location.href = `/pay?vNum=${plate}`;
      }
    } catch (error) {
      console.error("Error checking vehicle existence:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  const handleScan = () => {
    setShowModal(true);
    const { displayImage, apiImage } = captureImageFromVideo();
    setCroppedImage(displayImage);
    console.log(apiImage);
    fetch("https://number-plate-detection-three.vercel.app/detect-plate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ image: apiImage }),
    })
      .then((response) => response.json())
      .then((data) => {
        const numberPlate = data.plate_number.replace(/[^A-Z0-9]/g, "");
        console.log("API Response Text:", numberPlate);

        setTimeout(() => {
          checkVehicleExistence(numberPlate);
        }, 2000);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setShowModal(false);
        alert("Could not process the number plate.");
      });
  };

  const handleManualSubmit = () => {
    if (vNum.trim() !== "") {
      checkVehicleExistence(vNum.trim().toUpperCase());
    } else {
      alert("Please enter a valid vehicle number.");
    }
  };

  return (
    <div className="app-container">
      <div className="header" style={{ marginTop: "-20px" }}>
        <img src="/logo.png" alt="Logo" className="logo" />
        <div className="heading-text">
          <br />
          <h1 className="title">Smart Parking App</h1>
          <p className="caption">"Empowering Digital India .."</p>
        </div>
      </div>

      <div className="camera-box">
        <div className="camera-container">
          <video
            ref={videoRef}
            autoPlay
            playsInline // Ensure inline playback on iOS
            className="camera-feed enlarged-camera"
            style={{ width: "100%", height: "100%", objectFit: "cover" }} // Ensure video fits container
          />
          {showCameraButton && (
            <Button
              variant="contained"
              onClick={startCamera}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "#5b9bd5",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#4a8ac2" },
              }}
            >
              Start Camera
            </Button>
          )}
          <div className="scanner-guide"></div>
        </div>

        <Button
          variant="contained"
          onClick={handleScan}
          sx={{
            backgroundColor: "#5b9bd5",
            fontWeight: "bold",
            mt: 2,
            "&:hover": { backgroundColor: "#4a8ac2" },
            width: "100%",
            height: "40px",
          }}
        >
          SCAN VEHICLE NUMBER PLATE
        </Button>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        <TextField
          label="Enter Vehicle Number"
          variant="outlined"
          fullWidth
          value={vNum}
          onChange={(e) => setVNum(e.target.value)}
        />
        <Button
          variant="outlined"
          onClick={handleManualSubmit}
          sx={{
            mt: 2,
            width: "100%",
            fontWeight: "bold",
            color: "#5b9bd5",
            borderColor: "#5b9bd5",
            "&:hover": {
              backgroundColor: "#f0f8ff",
              borderColor: "#4a8ac2",
            },
          }}
        >
          CONTINUE
        </Button>
      </div>
      <br />
      <hr />
      <center>Copyright © 2025 - Apptech</center>
      <br />
      <center>
        <a href="/about">About The App & Policies</a>
      </center>

      <Modal open={showModal}>
        <Box
          sx={{
            backgroundColor: "#fff",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: "80%",
            maxWidth: 500,
            textAlign: "center",
            margin: "auto",
            mt: "20vh",
          }}
        >
          {croppedImage && <img src={croppedImage} alt="Cropped Plate" />}
          <CircularProgress sx={{ color: "#5b9bd5", mb: 2, mt: 2 }} />
          <Typography variant="h6">Fetching Vehicle Details</Typography>
        </Box>
      </Modal>
    </div>
  );
}

export default Home;